'use client';

import { AnimatePresence, Easing } from 'motion/react';
import * as m from 'motion/react-m';
import React, { ComponentProps, ReactNode, useState } from 'react';

import { LazyMotionWrapper } from 'src/general/components/LazyMotionWrapper/LazyMotionWrapper';

import { PerkContent } from '../PerkContent/PerkContent';

const TRANSITION_DURATION = 0.3;

const getVariants = (isLastTab: boolean) => ({
  initial: {
    opacity: 0,
    transform: isLastTab ? 'translate3d(-100%, 0, 0)' : 'translate3d(100%, 0, 0)',
  },
  animate: {
    opacity: 1,
    transform: 'translate3d(0, 0, 0)',
    transition: {
      duration: TRANSITION_DURATION,
      ease: 'easeInOut' as Easing,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: TRANSITION_DURATION,
      ease: 'easeInOut' as Easing,
    },
  },
});

type Tab = {
  label: string;
  content: ComponentProps<typeof PerkContent>;
};

type PerkSectionProps = {
  tabs: Tab[];
  footer?: ReactNode;
  onTabChange?: (index: number) => void;
};

export const PerkSection: React.FC<PerkSectionProps> = ({ tabs, footer, onTabChange }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  if (!tabs.length) {
    return null;
  }

  if (tabs.length < 2) {
    return (
      <div className="flex w-full flex-col">
        <PerkContent {...tabs[0].content} />
        <div>{footer}</div>
      </div>
    );
  }

  const isLastTab = selectedTab === tabs.length - 1;

  const onSelectTab = (index: number) => {
    setSelectedTab(index);
    onTabChange?.(index);
  };

  return (
    <div className="flex w-full flex-col">
      <div className="mb-6 flex w-full max-w-90 rounded-full border border-bor-neu-mut bg-bac-tie1">
        {tabs.map((tab, index) => (
          <button
            key={tab.label}
            className="relative w-full cursor-pointer border-none bg-transparent p-3 outline-none"
            onClick={() => onSelectTab(index)}
          >
            <LazyMotionWrapper>
              <AnimatePresence mode="wait" initial={false}>
                {selectedTab === index && (
                  <m.div
                    className="absolute inset-0 rounded-full border border-ind-800 bg-ind-50"
                    variants={getVariants(isLastTab)}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                  />
                )}
              </AnimatePresence>
            </LazyMotionWrapper>
            <div className="c-fs-body2 relative z-1 text-center">
              <span>{tab.label}</span>
            </div>
          </button>
        ))}
      </div>
      <PerkContent {...tabs[selectedTab].content} />
      <div>{footer}</div>
    </div>
  );
};
